import DockGenProviderSelect from "@/components/Table/DockGenProviderSelect.tsx";
import StoreLocationFields, {
    type StoreLocationData,
} from "@/components/Table/StoreLocationFields.tsx";
import { useFetchMailMergeForCase } from "@/queries/useFetchMailMergeForCase.ts";
import { type MrtParty, useFetchMrtPartiesForCase } from "@/queries/useFetchMrtPartiesForCase.ts";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid2 as Grid,
    LinearProgress,
    Typography,
} from "@mui/material";
import { CheckCircle, Clock, Download, FileText } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const DOCUMENTS = [
    {
        id: "UM LOR",
        title: "UM LOR",
        description:
            "Uninsured/Underinsured Motorist Letter of Representation - Formal notification to insurance carrier regarding UM/UIM claim representation.",
        filename: "UM_LOR.docx",
    },
    {
        id: "Non-correspondence Letter",
        title: "Non-correspondence Letter",
        description:
            "Standard letter requesting no direct communication with client - All correspondence must go through legal representation.",
        filename: "Non_Correspondence_Letter.docx",
    },
    {
        id: "ERISA Letter",
        title: "ERISA Letter",
        description:
            "Employee Retirement Income Security Act notification letter - Required for cases involving employer-sponsored health plans.",
        filename: "ERISA_Letter.docx",
    },
    {
        id: "Liability LOR",
        title: "Liability LOR",
        description:
            "Liability Letter of Representation - Formal notification to at-fault party's insurance carrier regarding legal representation.",
        filename: "Liability_LOR.docx",
    },
    {
        id: "Spoliation Letter",
        title: "Spoliation Letter",
        description:
            "Evidence preservation notice - Legal demand to preserve all relevant documents, records, and physical evidence.",
        filename: "Spoliation_Letter.docx",
    },
    {
        id: "Records Request Letter",
        title: "Records Request Letter",
        description:
            "Medical records request letter - Formal request to healthcare providers for complete medical records and documentation.",
        filename: "Records_Request_Letter.docx",
    },
    {
        id: "Medical Withdraw",
        title: "Medical Withdraw",
        description:
            "Formal withdrawal letter for medical providers - Notification of case closure and request for final billing statements.",
        filename: "Medical_Withdraw_Letter.docx",
        category: "withdraw",
    },
    {
        id: "Attorney Withdraw",
        title: "Attorney Withdraw",
        description:
            "Attorney withdrawal letter - Formal notification of attorney withdrawal from case representation.",
        filename: "Attorney_Withdraw_Letter.docx",
        category: "withdraw",
    },
    {
        id: "Insurance Withdraw",
        title: "Insurance Withdraw",
        description:
            "Insurance withdrawal letter - Formal notification to insurance carriers regarding case closure or withdrawal.",
        filename: "Insurance_Withdraw_Letter.docx",
        category: "withdraw",
    },
    {
        id: "Client Termination",
        title: "Client Termination",
        description:
            "Client termination letter - Formal notification of attorney-client relationship termination and case closure.",
        filename: "Client_Termination_Letter.docx",
        category: "withdraw",
    },
];

type DocumentStatus = {
    id: string;
    status: "ready" | "generating" | "generated";
    progress?: number;
};

type MedicalProvider = {
    id: string;
    companyName: string | null | undefined;
    address: string | null | undefined;
};

type Props = {
    foundCaseId: string;
    caseIdUUID: string;
    medicalProviders?: MedicalProvider[];
};

const DockGen = ({ caseIdUUID }: Props) => {
    const [documentStatuses, setDocumentStatuses] = useState<DocumentStatus[]>(
        DOCUMENTS.map((doc) => ({ id: doc.id, status: "ready" })),
    );

    const [templateName, setTemplateName] = useState("");
    const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(null);
    const [selectedProviderUUID, setselectedProviderUUID] = useState<string>("");
    const [selectedMedicalWithdrawProviderUUID, setSelectedMedicalWithdrawProviderUUID] =
        useState<string>("");
    const [selectedInsuranceWithdrawProviderUUID, setSelectedInsuranceWithdrawProviderUUID] =
        useState<string>("");

    const [storeLocation, setStoreLocation] = useState<StoreLocationData>({
        insuredCompany: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        emailAddress: "",
        faxNumber: "",
        attn: "",
    });

    const { data, isFetching } = useFetchMailMergeForCase({
        caseIdUUID,
        templateName,
        selectedProviderUUID:
            templateName === "Records Request Letter"
                ? selectedProviderUUID
                : templateName === "Medical Withdraw"
                  ? selectedMedicalWithdrawProviderUUID
                  : templateName === "Insurance Withdraw"
                    ? selectedInsuranceWithdrawProviderUUID
                    : undefined,
        storeLocation,
    });

    const {
        data: { data: providers = [] } = {},
    } = useFetchMrtPartiesForCase({
        caseIdUUID,
    });

    const handleStoreLocationChange = (field: string, value: string) => {
        setStoreLocation((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const medicalProviders = useMemo(() => {
        return providers.map((provider: MrtParty) => {
            return {
                id: provider.id,
                companyName: provider.companyName,
                address: provider.address,
                roleName: provider.roleName,
            };
        });
    }, [providers]);

    // const liabilityProviders = useMemo(() => {
    //     return providers
    //         .filter((provider: MrtParty) => provider.roleName === 'Liability - Bodily Injury')
    //         .map((provider: MrtParty) => {
    //             return {
    //                 id: provider.id,
    //                 companyName: provider.companyName,
    //                 address: provider.address,
    //             };
    //         });
    // }, [providers])

    const insuranceProviders = useMemo(() => {
        return providers
            .filter(
                (provider: MrtParty) =>
                    provider.roleName === "Liability - Bodily Injury" ||
                    provider.roleName === "UM - Bodily Injury",
            )
            .map((provider: MrtParty) => {
                return {
                    id: provider.id,
                    companyName: provider.companyName,
                    address: provider.address,
                    roleName: provider.roleName,
                };
            });
    }, [providers]);

    const handleGenerateDocument = (documentId: string) => {
        // For Records Request Letter, check if provider is selected
        if (documentId === "Records Request Letter" && !selectedProviderUUID) {
            return;
        }

        // For Medical Withdraw, check if provider is selected
        if (documentId === "Medical Withdraw" && !selectedMedicalWithdrawProviderUUID) {
            return;
        }

        // For Insurance Withdraw, check if provider is selected
        if (documentId === "Insurance Withdraw" && !selectedInsuranceWithdrawProviderUUID) {
            return;
        }

        setCurrentDocumentId(documentId);
        setTemplateName(documentId);

        setDocumentStatuses((prev) =>
            prev.map((doc) =>
                doc.id === documentId ? { ...doc, status: "generating", progress: 0 } : doc,
            ),
        );
    };

    const handleDownload = () => {
        if (!data?.blob) {
            return;
        }

        try {
            const url = window.URL.createObjectURL(data.blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = data.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Download failed:", err);
        }
    };

    const getDocumentStatus = (documentId: string): DocumentStatus => {
        return (
            documentStatuses.find((doc) => doc.id === documentId) || {
                id: documentId,
                status: "ready",
            }
        );
    };

    const getStatusIcon = (status: "ready" | "generating" | "generated") => {
        switch (status) {
            case "ready":
                return <FileText size={16} />;
            case "generating":
                return <Clock size={16} />;
            case "generated":
                return <CheckCircle size={16} />;
        }
    };

    const getStatusLabel = (status: "ready" | "generating" | "generated") => {
        switch (status) {
            case "ready":
                return "Ready";
            case "generating":
                return "Generating...";
            case "generated":
                return "Generated";
        }
    };

    const getButtonText = (documentId: string, status: DocumentStatus["status"]) => {
        if (
            documentId === "Records Request Letter" &&
            !selectedProviderUUID &&
            status !== "generating"
        ) {
            return "Select Provider First";
        }

        if (
            documentId === "Medical Withdraw" &&
            !selectedMedicalWithdrawProviderUUID &&
            status !== "generating"
        ) {
            return "Select Provider First";
        }

        if (
            documentId === "Insurance Withdraw" &&
            !selectedInsuranceWithdrawProviderUUID &&
            status !== "generating"
        ) {
            return "Select Insurance Company First";
        }

        if (documentId === "Spoliation Letter" && status !== "generating") {
            if (status === "generated") {
                return "Download Again";
            }

            if (
                !(
                    storeLocation.address &&
                    storeLocation.city &&
                    storeLocation.state &&
                    storeLocation.zip
                )
            ) {
                return "Enter Store Information First";
            }

            return "Generate & Download";
        }

        switch (status) {
            case "generating":
                return "Generating...";
            default:
                return "Generate & Download";
        }
    };

    const isButtonDisabled = (documentId: string, status: DocumentStatus["status"]) => {
        if (status === "generating" || isFetching) {
            return true;
        }

        if (documentId === "Records Request Letter" && !selectedProviderUUID) {
            return true;
        }

        if (documentId === "Medical Withdraw" && !selectedMedicalWithdrawProviderUUID) {
            return true;
        }

        if (documentId === "Insurance Withdraw" && !selectedInsuranceWithdrawProviderUUID) {
            return true;
        }

        if (documentId === "Spoliation Letter") {
            return !(
                storeLocation.insuredCompany &&
                storeLocation.address &&
                storeLocation.city &&
                storeLocation.state &&
                storeLocation.zip
            );
        }

        return false;
    };

    useEffect(() => {
        if (data?.blob && currentDocumentId) {
            handleDownload();

            setDocumentStatuses((prev) =>
                prev.map((doc) =>
                    doc.id === currentDocumentId
                        ? { ...doc, status: "generated", progress: 100 }
                        : doc,
                ),
            );

            setTemplateName("");
            setCurrentDocumentId(null);
        }
    }, [data, currentDocumentId]);

    return (
        <Grid container spacing={2}>
            <Grid size={12}>
                <Typography
                    sx={{
                        fontSize: "1.25rem",
                        fontWeight: 500,
                        marginBottom: (theme) => theme.spacing(3),
                        color: (theme) => theme.palette.text.primary,
                    }}
                >
                    Document Generation
                </Typography>

                <Alert severity="info" sx={{ mb: 3 }}>
                    Generate case-specific documents with client and case information automatically
                    populated. Click "Generate & Download" to create and download each document.
                </Alert>
            </Grid>

            <Grid container spacing={3}>
                {DOCUMENTS.map((document) => {
                    const status = getDocumentStatus(document.id);
                    const isRecordsRequestLetter = document.id === "Records Request Letter";
                    const isMedicalWithdraw = document.id === "Medical Withdraw";
                    const isInsuranceWithdraw = document.id === "Insurance Withdraw";
                    const isSpoliationLetter = document.id === "Spoliation Letter";

                    return (
                        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={document.id}>
                            <Card
                                sx={{
                                    height: "100%",
                                    transition: "all 0.2s ease-in-out",
                                    cursor: "pointer",
                                    "&:hover": {
                                        boxShadow: (theme) => theme.shadows[4],
                                        transform: "translateY(-2px)",
                                    },
                                }}
                            >
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            marginBottom: (theme) => theme.spacing(2),
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "1rem",
                                                fontWeight: 500,
                                                color: (theme) => theme.palette.text.primary,
                                            }}
                                        >
                                            {document.title}
                                        </Typography>

                                        <Chip
                                            icon={getStatusIcon(status.status)}
                                            label={getStatusLabel(status.status)}
                                            size="small"
                                            sx={{ fontSize: "0.75rem", height: 24 }}
                                        />
                                    </Box>

                                    <Typography
                                        sx={{
                                            fontSize: "0.875rem",
                                            color: (theme) => theme.palette.text.secondary,
                                            marginBottom: (theme) => theme.spacing(2),
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {document.description}
                                    </Typography>

                                    {/* Store Location Fields - Only for Spoliation Letter */}
                                    {isSpoliationLetter && (
                                        <StoreLocationFields
                                            storeLocation={storeLocation}
                                            onStoreLocationChange={handleStoreLocationChange}
                                        />
                                    )}

                                    {/* Provider Selection - Records Request Letter */}
                                    {isRecordsRequestLetter && (
                                        <DockGenProviderSelect
                                            selectedProviderUUID={selectedProviderUUID}
                                            onProviderChange={setselectedProviderUUID}
                                            providers={medicalProviders}
                                        />
                                    )}

                                    {/* Provider Selection - Medical Withdraw */}
                                    {isMedicalWithdraw && (
                                        <DockGenProviderSelect
                                            selectedProviderUUID={
                                                selectedMedicalWithdrawProviderUUID
                                            }
                                            onProviderChange={
                                                setSelectedMedicalWithdrawProviderUUID
                                            }
                                            providers={medicalProviders}
                                        />
                                    )}

                                    {/* Provider Selection - Insurance Withdraw */}
                                    {isInsuranceWithdraw && (
                                        <DockGenProviderSelect
                                            selectedProviderUUID={
                                                selectedInsuranceWithdrawProviderUUID
                                            }
                                            onProviderChange={
                                                setSelectedInsuranceWithdrawProviderUUID
                                            }
                                            providers={insuranceProviders}
                                            label="Select Insurance Company"
                                        />
                                    )}

                                    {status.status === "generating" && (
                                        <Box
                                            sx={{
                                                marginTop: (theme) => theme.spacing(1),
                                                width: "100%",
                                            }}
                                        >
                                            <LinearProgress
                                                variant="indeterminate"
                                                sx={{ mb: 1 }}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                Generating document...
                                            </Typography>
                                        </Box>
                                    )}

                                    <Button
                                        variant={"contained"}
                                        color={"primary"}
                                        startIcon={
                                            status.status === "generated" ? (
                                                <Download size={16} />
                                            ) : (
                                                <FileText size={16} />
                                            )
                                        }
                                        onClick={() => handleGenerateDocument(document.id)}
                                        disabled={isButtonDisabled(document.id, status.status)}
                                        sx={{
                                            width: "100%",
                                            marginTop: (theme) => theme.spacing(1),
                                            textTransform: "none",
                                            backgroundColor:
                                                (isRecordsRequestLetter && !selectedProviderUUID) ||
                                                (isMedicalWithdraw &&
                                                    !selectedMedicalWithdrawProviderUUID) ||
                                                (isInsuranceWithdraw &&
                                                    !selectedInsuranceWithdrawProviderUUID)
                                                    ? "grey.400"
                                                    : undefined,
                                        }}
                                    >
                                        {getButtonText(document.id, status.status)}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            <Box sx={{ mt: 4 }}>
                <Typography variant="body2" color="text.secondary">
                    All generated documents will include the current case and client information
                    automatically.
                </Typography>
            </Box>
        </Grid>
    );
};

export default DockGen;
